/**
 * <shade-filter>
 * ---------------------------------------------------------------------------
 * Narrows the shades shown in the native Prestige variant picker by disabling
 * the option-value inputs that fall outside the selected group. Prestige's CSS
 * already hides a disabled option's swatch (`:disabled + .block-swatch`, and
 * the color-swatch / thumbnail equivalents), so disabling the input both hides
 * the control and prevents it being submitted.
 *
 * It does not own selection state: when the currently selected shade falls
 * outside the chosen group, it selects the first in-group shade by dispatching
 * a native `change` event on that option input. Prestige's VariantPicker
 * listens for that (delegated on `input[data-option-position]`) and handles
 * re-render, price, gallery and the `variant:change` event, so the native
 * picker stays the single source of truth for the cart.
 */
if (!window.customElements.get("shade-filter")) {
  class ShadeFilter extends HTMLElement {
    connectedCallback() {
      this.formId = this.getAttribute("form-id");
      this.optionPosition = this.getAttribute("option-position");

      const mapEl = this.querySelector("script[data-shade-map]");
      try {
        this.groupMap = JSON.parse(mapEl ? mapEl.textContent : "{}");
      } catch (e) {
        this.groupMap = {};
      }

      this.pills = Array.from(this.querySelectorAll(".shade-filter__pill"));
      this.pills.forEach((pill) => {
        pill.addEventListener("click", () => this.onPillClick(pill));
      });

      // Default active group: "All" if the all-pill is present and active.
      const activePill = this.querySelector(".shade-filter__pill.is-active");
      this.activeGroup = activePill
        ? activePill.getAttribute("data-shade-group")
        : "__all__";

      // Prestige re-renders the variant picker on selection (it calls
      // replaceWith on <variant-picker>), which discards our disabled flags.
      // After each re-render it fires `variant:change` on the form, so we
      // re-apply the active filter then. A flag prevents recursion when our
      // own auto-select triggers the event.
      this._reapplying = false;
      const form = this.form;
      if (form) {
        form.addEventListener("variant:change", () => {
          if (this._reapplying) return;
          if (this.activeGroup && this.activeGroup !== "__all__") {
            this.applyFilter(this.activeGroup, true);
          }
        });
        // Defensive: block keyboard selection of a muted (out-of-group) shade.
        form.addEventListener("change", (e) => {
          const input = e.target;
          if (
            input &&
            input.matches(`input[data-option-position="${this.optionPosition}"]`) &&
            input.getAttribute("data-shade-muted") === "true" &&
            input.checked
          ) {
            const replacement = this.inputs.find(
              (i) => i.getAttribute("data-shade-muted") !== "true"
            );
            if (replacement) {
              this._reapplying = true;
              replacement.checked = true;
              replacement.dispatchEvent(new Event("change", { bubbles: true }));
              setTimeout(() => {
                this._reapplying = false;
                this.reapplyDisabled(this.activeGroup);
              }, 0);
            }
          }
        });
      }
    }

    get form() {
      return (
        document.getElementById(this.formId) ||
        document.querySelector(`form#${CSS.escape(this.formId)}`)
      );
    }

    get inputs() {
      // Option inputs are associated to the form via the `form` attribute but
      // are rendered inside <variant-picker>, NOT inside the <form> element.
      // So query the whole document by attribute rather than scoping to the
      // form's descendants.
      const byForm = Array.from(
        document.querySelectorAll(
          `input[data-option-position="${this.optionPosition}"][form="${this.formId}"]`
        )
      );
      if (byForm.length) return byForm;
      // Fallback: nearest variant-picker for this form id.
      const picker =
        document.querySelector(`variant-picker[form-id="${this.formId}"]`) ||
        this.closest(".product-info, [class*=product-info]") ||
        document;
      return Array.from(
        picker.querySelectorAll(
          `input[data-option-position="${this.optionPosition}"]`
        )
      );
    }

    onPillClick(pill) {
      const group = pill.getAttribute("data-shade-group");
      this.pills.forEach((p) => p.classList.remove("is-active"));
      pill.classList.add("is-active");
      this.applyFilter(group);
    }

    applyFilter(group, reapply) {
      this.activeGroup = group;
      const showAll = group === "__all__";
      const inputs = this.inputs;
      let selectedStillVisible = false;
      let firstVisibleInput = null;

      inputs.forEach((input) => {
        const valueName = this.valueNameFor(input);
        const inGroup = showAll || this.groupMap[valueName] === group;

        this.setMuted(input, !inGroup);

        if (inGroup) {
          if (!firstVisibleInput) firstVisibleInput = input;
          if (input.checked) selectedStillVisible = true;
        }
      });

      if (!selectedStillVisible && firstVisibleInput) {
        // Mark that the upcoming variant:change is our own, so the listener
        // does not recurse. It is cleared on the next tick after the picker
        // has re-rendered and re-applied flags below.
        this._reapplying = true;
        firstVisibleInput.checked = true;
        firstVisibleInput.dispatchEvent(new Event("change", { bubbles: true }));
        // After the picker re-renders (microtask), re-apply disabled flags to
        // the fresh inputs and release the guard.
        setTimeout(() => {
          this._reapplying = false;
          this.reapplyDisabled(group);
        }, 0);
      } else if (reapply) {
        // No selection change needed; flags were already set on current inputs.
      }
    }

    labelFor(input) {
      if (input.id) {
        const byFor = document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (byFor) return byFor;
      }
      if (input.nextElementSibling && input.nextElementSibling.matches("label, a")) {
        return input.nextElementSibling;
      }
      return null;
    }

    setMuted(input, muted) {
      // Keep the swatch visible but greyed and non-selectable when out of group.
      // We do NOT disable the input (which would hide it via theme CSS); we mute
      // the label instead, so the full set stays on screen with no flash on the
      // picker's re-render.
      const label = this.labelFor(input);
      if (label) label.classList.toggle("shade-filter--muted", muted);
      // Guard selection: a muted radio must not become the checked variant.
      input.setAttribute("data-shade-muted", muted ? "true" : "false");
    }

    reapplyDisabled(group) {
      const showAll = group === "__all__";
      this.inputs.forEach((input) => {
        const valueName = this.valueNameFor(input);
        const inGroup = showAll || this.groupMap[valueName] === group;
        this.setMuted(input, !inGroup);
      });
    }

    valueNameFor(input) {
      // The map is keyed on the option value's visible label, lowercased.
      // For swatch markup the label is <label class="color-swatch" for="id">
      // containing an .sr-only span with the shade name. For block markup the
      // name is in a bare span. Resolve the label via for/id first, then fall
      // back to the next sibling.
      let labelEl = null;
      if (input.id) {
        labelEl = document.querySelector(`label[for="${CSS.escape(input.id)}"], a[href][id="${CSS.escape(input.id)}"]`);
      }
      if (!labelEl && input.nextElementSibling && input.nextElementSibling.matches("label, a")) {
        labelEl = input.nextElementSibling;
      }
      if (!labelEl) return "";

      // Prefer the .sr-only span (swatch) or a non-swatch-colour span (block),
      // else the label's own text.
      const srOnly = labelEl.querySelector(".sr-only");
      const textSpan = Array.from(labelEl.querySelectorAll("span")).find(
        (sp) => !sp.classList.contains("block-swatch__color")
      );
      const text = (srOnly ? srOnly.textContent : (textSpan ? textSpan.textContent : labelEl.textContent)) || "";
      return text.toLowerCase().trim();
    }
  }

  window.customElements.define("shade-filter", ShadeFilter);
}
